import React, { useEffect, useState } from "react";
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Edit, Key, Trash2 } from "lucide-react";

// Définition des couleurs par rôle avec 50% d'opacité
const roleColors = {
  superadmin: "bg-red-600/50",
  projectmanager: "bg-blue-600/50",
  "charge-affaire": "bg-green-600/50",
  client: "bg-yellow-500/50",
};

const UserList = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchRole, setSearchRole] = useState("");

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const snapshot = await getDocs(collection(db, "users"));
        const usersData = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setUsers(usersData);
      } catch (error) {
        console.error("Erreur lors de la récupération des utilisateurs :", error);
      }
      setLoading(false);
    };
    fetchUsers();
  }, []);

  // Filtrage des utilisateurs par mot-clé et par rôle
  const filteredUsers = users.filter((user) => {
    const keyword = searchKeyword.toLowerCase();
    const matchesKeyword =
      (user.firstName && user.firstName.toLowerCase().includes(keyword)) ||
      (user.lastName && user.lastName.toLowerCase().includes(keyword)) ||
      (user.email && user.email.toLowerCase().includes(keyword)) ||
      (user.role && user.role.toLowerCase().includes(keyword));
    const matchesRole =
      searchRole === "" ||
      (user.role && user.role.toLowerCase() === searchRole.toLowerCase());
    return matchesKeyword && matchesRole;
  });

  const handleDeleteUser = async (userId) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")) {
      try {
        await deleteDoc(doc(db, "users", userId));
        setUsers(users.filter((u) => u.id !== userId));
      } catch (error) {
        console.error("Erreur lors de la suppression de l'utilisateur :", error);
      }
    }
  };

  // Fonction simulée pour changer le mot de passe (action réservée au Super Admin)
  const handleChangePassword = async (userId) => {
    const newPassword = window.prompt("Entrez le nouveau mot de passe pour cet utilisateur :");
    if (!newPassword) return;
    try {
      // Ici, vous pouvez appeler une Cloud Function ou l'API Admin pour changer le mot de passe
      alert(`Le mot de passe de l'utilisateur ${userId} a été mis à jour avec : ${newPassword}`);
    } catch (error) {
      console.error("Erreur lors du changement de mot de passe :", error);
      alert("Erreur lors du changement de mot de passe.");
    }
  };

  if (loading) return <div className="p-8">Chargement des utilisateurs...</div>;

  return (
    <div className="p-8">
      <h2 className="text-3xl font-bold mb-6 text-center">Liste des utilisateurs</h2>
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <input
          type="text"
          placeholder="Rechercher par mot-clé..."
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          className="w-full sm:w-1/2 border rounded px-3 py-2 text-sm transition"
        />
        <select
          value={searchRole}
          onChange={(e) => setSearchRole(e.target.value)}
          className="w-full sm:w-1/2 border rounded px-3 py-2 text-sm transition"
        >
          <option value="">Tous les rôles</option>
          <option value="superadmin">Super Admin</option>
          <option value="projectmanager">Chef de Projet</option>
          <option value="charge-affaire">Chargé(e) d'affaire</option>
          <option value="client">Client</option>
        </select>
      </div>

      {filteredUsers.length === 0 ? (
        <p className="text-sm">Aucun utilisateur trouvé.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredUsers.map((user) => {
            const roleKey = user.role ? user.role.toLowerCase() : "client";
            const bgColor = roleColors[roleKey] || "bg-gray-600/50";
            return (
              <div
                key={user.id}
                className={`border rounded-lg p-4 shadow hover:shadow-lg transition transform hover:scale-105 duration-300 ${bgColor} text-white`}
              >
                <h3 className="text-lg font-semibold mb-1">
                  {user.firstName} {user.lastName}
                </h3>
                <p className="text-sm mb-1">
                  <strong>Email :</strong> {user.email}
                </p>
                <p className="text-sm mb-2">
                  <strong>Rôle :</strong> {user.role || "Non défini"}
                </p>
                <div className="flex justify-end space-x-2">
                  {currentUser && currentUser.role.toLowerCase() === "superadmin" && (
                    <>
                      <Link
                        to={`/admin/edit-user/${user.id}`}
                        className="inline-flex items-center space-x-1 bg-gradient-to-r from-blue-400 to-blue-500 text-white px-3 py-1 text-sm rounded-[25px] shadow-md hover:shadow-lg transition duration-300 transform hover:-translate-y-0.5"
                      >
                        <Edit size={16} />
                        <span>Modifier</span>
                      </Link>
                      <button
                        onClick={() => handleChangePassword(user.id)}
                        className="inline-flex items-center space-x-1 bg-gradient-to-r from-purple-400 to-purple-500 text-white px-3 py-1 text-sm rounded-[25px] shadow-md hover:shadow-lg transition duration-300 transform hover:-translate-y-0.5"
                      >
                        <Key size={16} />
                        <span>Mot de passe</span>
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="inline-flex items-center space-x-1 bg-gradient-to-r from-red-400 to-red-500 text-white px-3 py-1 text-sm rounded-[25px] shadow-md hover:shadow-lg transition duration-300 transform hover:-translate-y-0.5"
                      >
                        <Trash2 size={16} />
                        <span>Supprimer</span>
                      </button>
                    </>
                  )}
                  {currentUser &&
                    currentUser.role.toLowerCase() === "charge-affaire" &&
                    user.role &&
                    user.role.toLowerCase() === "client" && (
                      <Link
                        to={`/admin/edit-user/${user.id}`}
                        className="inline-flex items-center space-x-1 bg-gradient-to-r from-blue-400 to-blue-500 text-white px-3 py-1 text-sm rounded-[25px] shadow-md hover:shadow-lg transition duration-300 transform hover:-translate-y-0.5"
                      >
                        <Edit size={16} />
                        <span>Modifier</span>
                      </Link>
                    )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(currentUser &&
        (currentUser.role.toLowerCase() === "superadmin" ||
          currentUser.role.toLowerCase() === "charge-affaire")) && (
        <div className="mt-4 text-center">
          <Link
            to="/admin/create-user"
            className="inline-block bg-blue-600 text-white px-3 py-1 text-sm rounded hover:bg-blue-700 transition"
          >
            Créer un nouvel utilisateur
          </Link>
        </div>
      )}
    </div>
  );
};

export default UserList;
